/**
 * Zod schemas for the Cultural Intel API (BE guide CULTURAL_INTEL_ENDPOINTS.md).
 *
 * These validate the raw responses; `components/CulturalIntelligence/utils/adaptCulturalReport.js`
 * then turns the report into the model the report components read. The skeleton is strict (ids,
 * titles, lists), so drift surfaces as a load error rather than a crash deep in the tree. The trend
 * detail sections (`demand`, `whoIsDrivingIt`, `whatPeopleSay`, `geo`, `adMarket`) pass through as
 * the ML sends them and are still a draft (CULTURAL_INTEL_TREND_DETAILS_RESPONSE.md, "a sample, not
 * a contract"), so they're only checked to be objects; the drawer reads them defensively.
 *
 * @module schemas/culturalIntelligence
 */

import { z } from 'zod';

/** First run, refresh and interpretation status. */
export const culturalStatusSchema = z.enum(['queued', 'processing', 'completed', 'failed']);

export const culturalErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  retryable: z.boolean(),
});

const subjectSchema = z.object({
  name: z.string(),
  category: z.string().nullable(),
  url: z.string(),
});

/** GET /cultural-intel/{id} */
export const culturalIntelStatusSchema = z.object({
  id: z.number(),
  status: culturalStatusSchema,
  subject: subjectSchema,
  period: z.string().nullable(),
  error: culturalErrorSchema.nullable(),
  refreshFrequency: z.enum(['weekly', 'monthly']).nullable(),
  nextRefreshAt: z.string().nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});

/** A card section as the ML sends it, or null when it didn't. */
const cardSectionSchema = z.record(z.string(), z.unknown()).nullable();

/**
 * The light card used everywhere in the report (top, culture blocks, global). The heavy drawer
 * sections come per trend from GET .../trends/{trendId} (`trendDetailSchema`).
 */
export const trendCardSchema = z.object({
  id: z.string(),
  shortLabel: z.string().nullable(),
  culture: z.string().nullable(),
  title: z.string(),
  status: z.string().nullable(),
  // The trend type (emerging / seasonal / persistent / peaking); replaces `status`.
  trendType: z
    .string()
    .nullable()
    .optional(),
  relevance: z.number().nullable(),
  // The score the UI calls "Opportunity score" (0–100); optional until every card carries it.
  opportunityScore: z
    .number()
    .nullable()
    .optional(),
  trendReach: z.number().nullable(),
  momentum: z.number().nullable(),
  acceleration: z.string().nullable(),
  description: z.string().nullable(),
  // ML's monthly series, oldest first ({ date, magnitude, vectorPct, searchVolume, … }), passed
  // through raw; the adapter keeps the usable points.
  trajectory: z
    .array(z.record(z.string(), z.unknown()))
    .nullable()
    .optional(),
  interpretation: z.object({ id: z.number(), status: culturalStatusSchema }).nullable(),
});

/** GET /cultural-intel/{id}/trends/{trendId}: the card plus the five drawer sections. */
export const trendDetailSchema = trendCardSchema.extend({
  culturalIntelResponseId: z.number(),
  demand: cardSectionSchema,
  whoIsDrivingIt: cardSectionSchema,
  whatPeopleSay: cardSectionSchema,
  geo: cardSectionSchema,
  adMarket: cardSectionSchema,
  // The drawer's "Evidence" (Figma 3963:42453): why the trend holds up (`reasoning`) and the
  // sources behind it (a URL and its date each). Optional, so a detail without it still validates.
  supportingEvidence: z
    .array(
      z.object({
        reasoning: z
          .string()
          .nullable()
          .optional(),
        urls: z
          .array(
            z.object({
              urlToEvidenceSource: z
                .string()
                .nullable()
                .optional(),
              publishedAt: z
                .string()
                .nullable()
                .optional(),
            })
          )
          .nullable()
          .optional(),
      })
    )
    .nullable()
    .optional(),
});

/** GET /cultural-intel/{id}/report: the latest completed edition. */
export const culturalIntelReportSchema = z.object({
  requestId: z.number(),
  culturalIntelResponseId: z.number(),
  period: z.string().nullable(),
  subject: subjectSchema,
  verdict: z.object({
    headline: z.string().nullable(),
    summaryParts: z.array(z.object({ text: z.string(), emphasized: z.boolean() })),
  }),
  relevantTrends: z.array(trendCardSchema),
  trendsByCultures: z.array(
    z.object({
      name: z.string(),
      cultureRelevance: z.number().nullable(),
      relevantTrends: z.array(trendCardSchema),
      otherTrends: z.array(trendCardSchema),
    })
  ),
  globalTrends: z.array(trendCardSchema),
});

const nullableString = z.string().nullable();

/** What the drawer's interpretation tab shows; every key present, `null` when the ML didn't send it. */
export const interpretationContentSchema = z.object({
  interpretedFor: z.object({ client: nullableString }),
  confidence: z.object({
    band: z.enum(['high', 'medium', 'low']).nullable(),
    label: nullableString,
  }),
  actions: z.object({ mode: z.object({ value: nullableString, why: nullableString }) }),
  synthesis: nullableString,
  fit: z.object({ why: nullableString }),
  buyer: z.object({
    archetype: nullableString,
    demographicCore: nullableString,
    psychographicState: nullableString,
    decisionTrigger: nullableString,
    rejectSignal: nullableString,
  }),
  whitespace: z.object({
    status: z.enum(['open', 'contested', 'crowded']).nullable(),
    detail: nullableString,
    competitors: z.array(
      z.object({ name: nullableString, position: nullableString, url: nullableString })
    ),
  }),
});

/** POST interpretations (201 / 200) and GET interpretations/{id}. */
export const interpretationSchema = z.object({
  id: z.number(),
  culturalIntelResponseId: z.number(),
  status: culturalStatusSchema,
  trendId: z.string(),
  error: culturalErrorSchema.nullable().optional(),
  respData: interpretationContentSchema.nullable().optional(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
});

/** @typedef {import('zod').infer<typeof culturalIntelStatusSchema>} CulturalIntelStatus */
/** @typedef {import('zod').infer<typeof culturalIntelReportSchema>} CulturalIntelReport */
/** @typedef {import('zod').infer<typeof trendCardSchema>} CulturalTrendCard */
/** @typedef {import('zod').infer<typeof trendDetailSchema>} CulturalTrendDetail */
/** @typedef {import('zod').infer<typeof interpretationSchema>} CulturalInterpretation */
